import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Trophy, Users, Calendar, Loader2, LayoutGrid, List, Search,
  Droplets, Beef, CandyOff, Dumbbell, Footprints, Moon, Award, Flame, Star, Target, Zap,
  Filter, Download, Eye, EyeOff, TrendingUp, Copy
} from 'lucide-react';

// Icon mapping for badges
const iconMap = {
  droplets: Droplets,
  beef: Beef,
  'candy-off': CandyOff,
  dumbbell: Dumbbell,
  footprints: Footprints,
  moon: Moon,
  award: Award,
  flame: Flame,
  star: Star,
  target: Target,
  zap: Zap,
  trophy: Trophy
};

const milestoneTypeLabels = {
  action_streak: 'Action Streak',
  checkin_streak: 'Check-in Streak',
  full_send_day: 'Full Send Day',
  full_send_month: 'Full Send Month',
  points: 'Points Milestone',
  comeback: 'Comeback'
};

const AdminGroupWins = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('timeline');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showOptedOut, setShowOptedOut] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [fistbumpingId, setFistbumpingId] = useState(null);

  const fetchMilestones = useCallback(async () => {
    setLoading(true);
    try {
      // Use admin endpoint that includes opted-out users
      const res = await apiClient.get(`/admin/group-wins?view=${view}&include_opted_out=${showOptedOut}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch group wins:', err);
      toast.error('Failed to load group wins');
    } finally {
      setLoading(false);
    }
  }, [view, showOptedOut]);

  useEffect(() => {
    fetchMilestones();
  }, [fetchMilestones]);

  const handleFistbump = async (achievementId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFistbumpingId(achievementId);
    try {
      const res = await apiClient.post(`/group-milestones/${achievementId}/fistbump`);
      toast.success(res.data.message);
      // Optimistically update local state so the count/toggle updates instantly
      setData((prev) => {
        if (!prev) return prev;
        const bumpedNow = !!res.data.fistbumped;
        const delta = bumpedNow ? 1 : -1;
        const updateAch = (a) =>
          a.id === achievementId
            ? { ...a, fistbump_count: Math.max(0, (a.fistbump_count || 0) + delta), user_fistbumped: bumpedNow }
            : a;
        if (prev.view === 'grouped') {
          return {
            ...prev,
            users: (prev.users || []).map((u) => ({
              ...u,
              achievements: (u.achievements || []).map(updateAch),
            })),
          };
        }
        return {
          ...prev,
          achievements: (prev.achievements || []).map(updateAch),
        };
      });
    } catch (err) {
      console.error('Fist bump failed:', err);
      toast.error('Failed to send fist bump');
    } finally {
      setFistbumpingId(null);
    }
  };

  const syncBadges = async () => {
    setSyncing(true);
    try {
      const res = await apiClient.post('/admin/group-wins/sync');
      toast.success(res.data.message);
      fetchMilestones(); // Refresh the list
    } catch (err) {
      toast.error('Failed to sync badges');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = dateString.includes('T') 
      ? new Date(dateString) 
      : new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDisplayDate = (achievement) => {
    return achievement.for_date || achievement.earned_at;
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Trophy;
    return <IconComponent size={18} />;
  };

  const copyShareText = () => {
    const text = `🎉 So far in ${data?.month}, our community has achieved ${data?.total_count} milestone${data?.total_count !== 1 ? 's' : ''}! Keep crushing it! 💪`;
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Filter achievements (timeline view)
  const filteredAchievements = data?.achievements?.filter(a => {
    const matchesSearch = searchTerm === '' || 
      a.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.badge_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || a.milestone_type === filterType;
    return matchesSearch && matchesType;
  }) || [];

  // Filter users (grouped view) - keep users whose name/any-achievement matches
  const filteredUsers = data?.users?.filter(u => {
    const nameMatch = searchTerm === '' || u.user_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const hasType = filterType === 'all' || (u.achievements || []).some(a => a.milestone_type === filterType);
    return nameMatch && hasType;
  }) || [];

  // Group by type for stats — works for both response shapes
  const statsByType = {};
  const statsSource = data?.achievements || (data?.users || []).flatMap(u => u.achievements || []);
  statsSource.forEach(a => {
    const type = a.milestone_type || 'other';
    statsByType[type] = (statsByType[type] || 0) + 1;
  });

  const hasResults = view === 'timeline' ? filteredAchievements.length > 0 : filteredUsers.length > 0;
  const shownCount = view === 'timeline' ? filteredAchievements.length : filteredUsers.reduce((s, u) => s + (u.achievements || []).length, 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#EFB342] animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#EFB342]/20 rounded-2xl flex items-center justify-center">
              <Trophy className="text-[#EFB342]" size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-900">Group Wins</h1>
              <p className="text-stone-600">{data?.month} • Community Milestones</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={syncBadges}
              disabled={syncing}
              variant="outline"
              className="border-stone-300 text-stone-700 hover:bg-stone-100"
              title="Sync badges awarded this month to Group Wins"
            >
              {syncing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
              Sync Badges
            </Button>
            <Button
              onClick={copyShareText}
              className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
            >
              <Copy size={16} className="mr-2" />
              Copy Share Text
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-[#EFB342]/20 to-[#EFB342]/5 border border-[#EFB342]/30 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="text-[#EFB342]" size={24} />
              <span className="text-stone-600 text-sm">Total Milestones</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">{data?.total_count || 0}</p>
          </div>
          
          <div className="bg-white border border-stone-200 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <Users className="text-blue-400" size={24} />
              <span className="text-stone-600 text-sm">Users with Wins</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">{data?.unique_users || 0}</p>
          </div>
          
          <div className="bg-white border border-stone-200 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <Flame className="text-orange-400" size={24} />
              <span className="text-stone-600 text-sm">Full Send Days</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">{statsByType['full_send_day'] || 0}</p>
          </div>
          
          <div className="bg-white border border-stone-200 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="text-green-400" size={24} />
              <span className="text-stone-600 text-sm">Streak Milestones</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">
              {(statsByType['action_streak'] || 0) + (statsByType['checkin_streak'] || 0)}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-stone-200 rounded-2xl p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-wrap gap-3 items-center">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" size={16} />
                <Input
                  placeholder="Search user or badge..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 bg-stone-100 border-stone-300 text-stone-900 w-64"
                />
              </div>
              
              {/* Type Filter */}
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-48 bg-stone-100 border-stone-300 text-stone-900">
                  <Filter size={14} className="mr-2" />
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="bg-stone-100 border-stone-300">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="action_streak">Action Streaks</SelectItem>
                  <SelectItem value="checkin_streak">Check-in Streaks</SelectItem>
                  <SelectItem value="full_send_day">Full Send Days</SelectItem>
                  <SelectItem value="full_send_month">Full Send Month</SelectItem>
                  <SelectItem value="points">Points Milestones</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Include Opted Out Toggle */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOptedOut(!showOptedOut)}
                className={`border-stone-300 ${showOptedOut ? 'text-stone-900 bg-stone-100' : 'text-stone-500'}`}
              >
                {showOptedOut ? <Eye size={14} className="mr-2" /> : <EyeOff size={14} className="mr-2" />}
                {showOptedOut ? 'Showing All' : 'Hiding Opted-Out'}
              </Button>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
              <button
                onClick={() => setView('timeline')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${
                  view === 'timeline' 
                    ? 'bg-[#EFB342] text-black font-medium' 
                    : 'text-stone-600 hover:text-stone-900'
                }`}
              >
                <List size={14} />
                Timeline
              </button>
              <button
                onClick={() => setView('grouped')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${
                  view === 'grouped' 
                    ? 'bg-[#EFB342] text-black font-medium' 
                    : 'text-stone-600 hover:text-stone-900'
                }`}
              >
                <LayoutGrid size={14} />
                By User
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-stone-600 text-sm">
            Showing {shownCount} of {data?.total_count || 0} milestones
          </p>
        </div>

        {/* Content */}
        {!hasResults ? (
          <div className="text-center py-16 bg-[#F0EBE1] rounded-2xl border border-stone-200">
            <Trophy className="mx-auto text-stone-400 mb-4" size={48} />
            <p className="text-stone-600 text-lg mb-2">No milestones found</p>
            <p className="text-stone-500 text-sm">
              {searchTerm || filterType !== 'all' 
                ? 'Try adjusting your filters' 
                : 'Milestones will appear here as users achieve them'}
            </p>
          </div>
        ) : view === 'timeline' ? (
          /* Timeline View */
          <div className="space-y-3">
            {filteredAchievements.map((achievement) => (
              <div 
                key={achievement.id}
                className={`bg-white border rounded-xl p-4 hover:border-stone-300 transition-all ${
                  achievement.is_opted_out ? 'border-stone-200 opacity-60' : 'border-stone-200'
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Badge Icon */}
                  <div className="w-12 h-12 bg-gradient-to-br from-[#EFB342]/20 to-[#EFB342]/5 rounded-xl flex items-center justify-center text-[#EFB342] flex-shrink-0">
                    {getIcon(achievement.badge_icon)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-stone-900 font-medium">{achievement.user_name}</p>
                      {achievement.is_opted_out && (
                        <span className="text-xs bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <EyeOff size={10} />
                          Hidden
                        </span>
                      )}
                    </div>
                    <p className="text-[#EFB342] font-semibold">{achievement.badge_name}</p>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-stone-500 text-xs flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(getDisplayDate(achievement))}
                      </span>
                      <span className="text-stone-400 text-xs">
                        {milestoneTypeLabels[achievement.milestone_type] || achievement.milestone_type}
                      </span>
                    </div>
                  </div>
                  
                  {/* Fist Bump Button (admin can send from here) */}
                  <button
                    type="button"
                    onClick={(e) => handleFistbump(achievement.id, e)}
                    disabled={fistbumpingId === achievement.id}
                    data-testid={`admin-fistbump-${achievement.id}`}
                    title={achievement.user_fistbumped ? 'Remove your fist bump' : 'Send a fist bump'}
                    className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl border transition-all ${
                      achievement.user_fistbumped
                        ? 'bg-[#EFB342]/20 border-[#EFB342]/60 text-[#EFB342]'
                        : 'bg-stone-100/60 border-stone-300 text-stone-900 hover:bg-stone-100 hover:border-[#EFB342]/40'
                    } ${fistbumpingId === achievement.id ? 'opacity-70 cursor-wait' : 'cursor-pointer'}`}
                  >
                    <span className="text-2xl font-bold flex items-center gap-1">
                      {fistbumpingId === achievement.id ? (
                        <Loader2 className="animate-spin" size={20} />
                      ) : (
                        <>👊 {achievement.fistbump_count || 0}</>
                      )}
                    </span>
                    <span className="text-xs text-stone-500">
                      {achievement.user_fistbumped ? 'you bumped' : 'fist bumps'}
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Grouped by User View */
          <div className="space-y-6">
            {filteredUsers.map((userData) => (
              <div key={userData.user_id} className={`bg-[#F0EBE1] border rounded-2xl p-5 ${
                userData.is_opted_out ? 'border-stone-200 opacity-60' : 'border-stone-200'
              }`}>
                <div className="flex items-center gap-4 mb-4 pb-4 border-b border-stone-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#EFB342] to-[#D99F30] rounded-full flex items-center justify-center text-black font-bold text-lg">
                    {userData.user_name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-stone-900 font-semibold text-lg">{userData.user_name}</p>
                      {userData.is_opted_out && (
                        <span className="text-xs bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <EyeOff size={10} />
                          Hidden from public
                        </span>
                      )}
                    </div>
                    <p className="text-stone-500 text-sm">{userData.achievements.length} milestone{userData.achievements.length !== 1 ? 's' : ''} this month</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-[#EFB342]">
                      👊 {userData.achievements.reduce((sum, a) => sum + (a.fistbump_count || 0), 0)}
                    </p>
                    <p className="text-xs text-stone-500">total fist bumps</p>
                  </div>
                </div>
                
                <div className="grid gap-3">
                  {userData.achievements
                    .filter(a => filterType === 'all' || a.milestone_type === filterType)
                    .map((achievement) => (
                    <div key={achievement.id} className="flex items-center gap-3 bg-stone-50 rounded-lg p-3">
                      <div className="w-10 h-10 bg-[#EFB342]/10 rounded-lg flex items-center justify-center text-[#EFB342]">
                        {getIcon(achievement.badge_icon)}
                      </div>
                      <div className="flex-1">
                        <p className="text-stone-900 font-medium">{achievement.badge_name}</p>
                        <p className="text-stone-500 text-xs">{formatDate(getDisplayDate(achievement))}</p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleFistbump(achievement.id, e)}
                        disabled={fistbumpingId === achievement.id}
                        data-testid={`admin-fistbump-grouped-${achievement.id}`}
                        title={achievement.user_fistbumped ? 'Remove your fist bump' : 'Send a fist bump'}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                          achievement.user_fistbumped
                            ? 'bg-[#EFB342]/20 border-[#EFB342]/60 text-[#EFB342]'
                            : 'bg-white/60 border-stone-300 text-stone-900 hover:bg-white hover:border-[#EFB342]/40'
                        } ${fistbumpingId === achievement.id ? 'opacity-70 cursor-wait' : 'cursor-pointer'}`}
                      >
                        {fistbumpingId === achievement.id ? (
                          <Loader2 className="animate-spin" size={14} />
                        ) : (
                          <>👊 {achievement.fistbump_count || 0}</>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminGroupWins;
